/**
 * Storage Service
 * Provides unified storage API that works on both web (localStorage) and native (Capacitor Preferences)
 * 
 * On Web: Uses localStorage
 * On Native: Uses @capacitor/preferences for secure, persistent storage
 */

import { isNative } from './platformDetector';

let preferencesPlugin = null;

// Lazy load Capacitor Preferences plugin
const getPreferences = async () => {
  if (preferencesPlugin === null && isNative()) {
    try {
      const module = await import('@capacitor/preferences');
      preferencesPlugin = module.Preferences;
    } catch (e) {
      console.warn('Preferences plugin not available, falling back to localStorage');
      preferencesPlugin = false;
    }
  }
  return preferencesPlugin;
};

/**
 * Get item from storage
 * @param {string} key - Storage key
 * @returns {Promise<string|null>} - Stored value or null
 */
export const getItem = async (key) => {
  if (isNative()) {
    const Preferences = await getPreferences();
    if (Preferences) {
      const { value } = await Preferences.get({ key });
      return value;
    }
  }
  return localStorage.getItem(key);
};

/**
 * Set item in storage
 * @param {string} key - Storage key
 * @param {string} value - Value to store
 * @returns {Promise<void>}
 */
export const setItem = async (key, value) => {
  if (isNative()) {
    const Preferences = await getPreferences();
    if (Preferences) {
      await Preferences.set({ key, value: String(value) });
      return;
    }
  }
  localStorage.setItem(key, value);
};

/**
 * Remove item from storage
 * @param {string} key - Storage key
 * @returns {Promise<void>}
 */
export const removeItem = async (key) => {
  if (isNative()) {
    const Preferences = await getPreferences();
    if (Preferences) {
      await Preferences.remove({ key });
      return;
    }
  }
  localStorage.removeItem(key);
};

/**
 * Clear all storage
 * @returns {Promise<void>}
 */
export const clear = async () => {
  if (isNative()) {
    const Preferences = await getPreferences();
    if (Preferences) {
      await Preferences.clear();
      return;
    }
  }
  localStorage.clear();
};

/**
 * Get all keys in storage
 * @returns {Promise<string[]>}
 */
export const keys = async () => {
  if (isNative()) {
    const Preferences = await getPreferences();
    if (Preferences) {
      const { keys: storageKeys } = await Preferences.keys();
      return storageKeys;
    }
  }
  return Object.keys(localStorage);
};

/**
 * Get item synchronously (web only, for backward compatibility)
 * Note: On native, this will return null - use async getItem instead
 * @param {string} key - Storage key
 * @returns {string|null}
 */
export const getItemSync = (key) => {
  return localStorage.getItem(key);
};

/**
 * Set item synchronously (web only, for backward compatibility)
 * Note: On native, this will only update localStorage - use async setItem instead
 * @param {string} key - Storage key
 * @param {string} value - Value to store
 */
export const setItemSync = (key, value) => {
  localStorage.setItem(key, value);
};

/**
 * Remove item synchronously (web only, for backward compatibility)
 * @param {string} key - Storage key
 */
export const removeItemSync = (key) => {
  localStorage.removeItem(key);
};

const storageService = {
  getItem,
  setItem,
  removeItem,
  clear,
  keys,
  // Sync methods for backward compatibility
  getItemSync,
  setItemSync,
  removeItemSync
};

export default storageService;
